import { isValidAddress } from '../../lib/address/validator';
import { addressFormat } from '../../lib/address/formatter';
/**
 * Immutable Address class that encapsulates address strings with network context.
 * Provides consistent formatting, validation, and display methods.
 *
 * @example
 * ```typescript
 * const addr = new Address('0x1234...', network);
 * addr.toShortString(); // "0x123...5678"
 * Address.isValid('0x1234...', { name: 'ETHEREUM_MAINNET' }); // true
 * ```
 */
export class Address {
    constructor(address, network, providerName) {
        if (!network && !providerName) {
            throw new Error('Address requires either network or providerName');
        }
        this._raw = address || '';
        this._network = network;
        this._providerName = providerName;
        this._normalized = addressFormat({ address: this._raw, network, providerName: this._providerName });
    }
    /**
     * Get the raw, unmodified address string
     */
    get raw() {
        return this._raw;
    }
    /**
     * Get the normalized address (formatted for the network)
     */
    get normalized() {
        return this._normalized;
    }
    /**
     * Get the full address
     */
    get full() {
        return this._normalized;
    }
    /**
     * Get the associated network
     */
    get network() {
        return this._network;
    }
    /**
     * Check if this address is valid for its network
     */
    static isValid(address, network = null, providerName) {
        return isValidAddress({ address, network, providerName });
    }
    /**
     * Return the first network in the list that `address` is valid for, or undefined.
     * Dedupes probes by NetworkType so we only validate once per chain family.
     */
    static resolveNetwork(address, networks) {
        if (!address)
            return undefined;
        const seen = new Set();
        return networks.find(n => {
            if (seen.has(n.type))
                return false;
            seen.add(n.type);
            return Address.isValid(address, n);
        });
    }
    /**
     * Format address as shortened display: first5...last4
     * @returns Shortened address (e.g., "0x123...5678")
     */
    toShortString() {
        const addr = this._normalized;
        if (!addr || addr.length < 13) {
            return this.full;
        }
        const shortened = `${addr.substring(0, 5)}...${addr.substring(addr.length - 4)}`;
        return shortened;
    }
    /**
     * Format address as ending only: ...last4
     * @returns Last 4 characters with ellipsis (e.g., "...5678")
     */
    toEndingString() {
        const addr = this._normalized;
        if (!addr)
            return '';
        return `...${addr.substring(addr.length - 4)}`;
    }
    /**
     * Get address parts for emphasized display (bold first4 and last4)
     * Used for rendering with different styles for start/middle/end
     * @returns Object with start, middle, end parts
     */
    toEmphasizedParts() {
        const addr = this._normalized;
        if (!addr || addr.length <= 8) {
            return { start: addr, middle: '', end: '' };
        }
        return {
            start: addr.slice(0, 5),
            middle: addr.slice(5, -4),
            end: addr.slice(-4)
        };
    }
    /**
     * Get seed number for icon generation (chars 2-10 as hex integer)
     * Used by AddressIcon component with Jazzicon
     * @returns Integer seed for deterministic icon generation
     */
    static toIconSeed(address) {
        if (!address || address.length < 10)
            return 0;
        return parseInt(address.slice(2, 10), 16);
    }
    /**
     * Static factory method for emails (exchange accounts)
     * Returns an EmailAddress instance with email-specific formatting
     * @param email - Email address string
     * @param maxNameLength - Maximum length for email name before shortening (default: 14)
     */
    static fromEmail(email, maxNameLength = 14) {
        return new EmailAddress(email, maxNameLength);
    }
    /**
     * Convert to string (default: full format)
     */
    toString() {
        return this.full;
    }
    /**
     * Static method to compare two address strings with network context
     * More efficient than creating Address instances when you just need comparison
     * @param addr1 - First address string
     * @param addr2 - Second address string
     * @param network - Optional network context for both addresses
     * @param providerName - Optional provider name for both addresses
     * @returns true if addresses are equivalent after normalization
     */
    static equals(addr1, addr2, network, providerName) {
        if (!addr1 || !addr2)
            return false;
        const norm1 = addressFormat({ address: addr1, network, providerName });
        const norm2 = addressFormat({ address: addr2, network, providerName });
        return norm1 === norm2;
    }
}
/**
 * Special class for email addresses (exchange accounts)
 * Handles email-specific shortening logic
 */
export class EmailAddress {
    constructor(email, maxNameLength = 14) {
        this._email = email || '';
        this._maxNameLength = maxNameLength;
    }
    /**
     * Format email with shortened name if necessary
     * Keeps domain intact, shortens long names with ellipsis
     * @returns Shortened email (e.g., "verylong...name@example.com")
     */
    toShortString() {
        const [name, domain] = this._email.split('@');
        if (!domain)
            return this._email; // Invalid email, return as-is
        const len = name.length;
        if (len <= this._maxNameLength) {
            return this._email;
        }
        const shortName = name.substring(0, Math.floor((this._maxNameLength / 3) * 2)) +
            '...' +
            name.substring(len - Math.floor(this._maxNameLength / 3), len);
        return `${shortName}@${domain}`;
    }
}
/**
 * Type guard to check if address is an EmailAddress
 */
export function isEmailAddress(address) {
    return address instanceof EmailAddress;
}
